import { ComposerPlanProgressRow } from "./ComposerPlanProgress";
import type { ComposerPlanProgress } from "./deriveComposerPlanProgress";
import type { ChatComposerHandle } from "./ChatComposerHandle";
import type {
  ApprovalRequestId,
  CmmdOrgProviderPolicy,
  EnvironmentId,
  ModelSelection,
  ProjectEntry,
  ProviderApprovalDecision,
  ProviderInteractionMode,
  ProviderKind,
  ResolvedKeybindingsConfig,
  RuntimeMode,
  ScopedThreadRef,
  ServerProvider,
  ThreadId,
  TurnId,
} from "@cmmd-forge/contracts";
import { normalizeModelSlug } from "@cmmd-forge/shared/model";
import {
  forwardRef,
  memo,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import { useQuery } from "@tanstack/react-query";
import { useDebouncedValue } from "@tanstack/react-pacer";
import { projectSearchEntriesQueryOptions } from "~/lib/projectReactQuery";
import {
  clampCollapsedComposerCursor,
  type ComposerTrigger,
  collapseExpandedComposerCursor,
  detectComposerTrigger,
  expandCollapsedComposerCursor,
  replaceTextRange,
} from "../../composer-logic";
import { deriveComposerSendState } from "../ChatView.logic";
import {
  type ComposerImageAttachment,
  type ComposerFileAttachment,
  type DraftThreadEnvMode,
  useComposerDraftStore,
  useComposerThreadDraft,
} from "../../composerDraftStore";
import type { DraftId } from "../../draftId";
import {
  type TerminalContextDraft,
  type TerminalContextSelection,
  insertInlineTerminalContextPlaceholder,
} from "../../lib/terminalContext";
import { type ComposerPromptEditorHandle } from "../ComposerPromptEditor";
import { getAvailableProviderOptions } from "./ProviderModelPicker";
import type { ModelPickerProviderAvailability } from "./modelPickerProviderAvailability";
import { buildSearchableModelOptions } from "./composerSearchableModelOptions";
import { type ComposerCommandItem } from "./ComposerCommandMenu";
import { useComposerSendGate } from "./composerCreditGateView";
import { ChatComposerBelowPrompt } from "./ChatComposerBelowPrompt";
import {
  selectBrowserCommentDraft,
  useBrowserCommentDraftStore,
} from "../browser/browserCommentDraftStore";
import { getComposerPromptLengthValidationMessage } from "./composerSubmission";
import {
  buildChatComposerContextFacts,
  resolveComposerThreadGoal,
  type ChatComposerPrInput,
  type RemoteComposerThreadGoal,
} from "./ComposerControlsStrip.logic";
import { buildChatComposerContextStrip } from "./ComposerControlsStrip";
import { ChatComposerHeaderBanner } from "./ChatComposerHeaderBanner";
import { RestingComposerControlsPortal } from "./RestingComposerControlsPortal";
import { useComposerImagePersistence } from "./useComposerImagePersistence";
import { useRestingComposer } from "./useRestingComposer";
import { ComposerSurface } from "./ComposerSurface";
import { useComposerStashIntegration } from "./useComposerStashIntegration";
import { resolveComposerMenuActiveItemId } from "./composerMenuHighlight";
import { useComposerMenuItemSelection } from "./useComposerMenuItemSelection";
import { synchronizeComposerPrompt } from "./synchronizeComposerPrompt";

const cannotOpenUsageLimits = () => false;
import { getComposerProviderState, renderProviderTraits } from "./composerProviderRegistry";
import { type ExpandedImagePreview } from "./ExpandedImagePreview";
import { sweepUnreferencedComposerFiles } from "../../composerFileAttachmentLifecycle";
import { useChatComposerAttachments } from "./useChatComposerAttachments";
import { useRemoveComposerTerminalContext } from "./useRemoveComposerTerminalContext";
import { ComposerFooter } from "./ComposerFooter";
import { useComposerFooterCompactness } from "./useComposerFooterCompactness";
import { useComposerModelSelection } from "./useComposerModelSelection";
import { randomUUID } from "~/lib/utils";
import { toastManager } from "../ui/toast";
import type { UnifiedSettings } from "@cmmd-forge/contracts/settings";
import type { SessionPhase, Thread } from "../../types";
import type { PendingUserInputDraftAnswer } from "../../pendingUserInput";
import type {
  GitHubActionRequiredRequest,
  PendingApproval,
  PendingUserInput,
} from "../../session-logic";
import { deriveLatestContextWindowSnapshot } from "../../lib/contextWindow";
import { useComposerPromptNavigation } from "./useComposerPromptNavigation";
import {
  buildComposerMenuItems,
  buildComposerMenuSearchKey,
  buildComposerModelOptionsByProvider,
  composerTriggersEqual,
  getComposerFooterActionLayoutKey,
  getComposerMenuEmptyState,
  getNextComposerMenuHighlightId,
  getComposerPromptValue,
  isComposerPathMenuLoading,
  resolveSelectedModelForPicker,
  shouldShowComposerImages,
  shouldShowPlanFollowUpPrimaryAction,
  syncTerminalContextsByIds,
  terminalContextIdListsEqual,
} from "./ChatComposer.logic";
import { derivePendingPrimaryAction } from "./pendingPrimaryAction";
import { getComposerPromptPlaceholder } from "./composerPlaceholder";
import { hasComposerHeader } from "./composerHeaderVisibility";

const COMPOSER_PATH_QUERY_DEBOUNCE_MS = 120;
const EMPTY_PROJECT_ENTRIES: ProjectEntry[] = [];
const EMPTY_FILES: readonly ComposerFileAttachment[] = [];
export interface ChatComposerProps {
  readonly contentPresentation?: "draft" | "submitted";
  composerDraftTarget: ScopedThreadRef | DraftId;
  environmentId: EnvironmentId;
  routeKind: "server" | "draft";
  routeThreadRef: ScopedThreadRef;
  draftId: DraftId | null;
  // Thread context
  activeThreadId: ThreadId | null;
  activeThreadEnvironmentId: EnvironmentId | undefined;
  activeThread: Thread | undefined;
  isServerThread: boolean;
  isLocalDraftThread: boolean;
  remoteGoal?: RemoteComposerThreadGoal | null;

  // Session phase
  phase: SessionPhase;
  isConnecting: boolean;
  isSendBusy: boolean;
  isPreparingWorktree: boolean;

  // Pending approvals / inputs
  activePendingApproval: PendingApproval | null;
  activeActionRequired: GitHubActionRequiredRequest | null;
  pendingApprovals: PendingApproval[];
  pendingUserInputs: PendingUserInput[];
  activePendingProgress: {
    questionIndex: number;
    isLastQuestion: boolean;
    canAdvance: boolean;
    customAnswer: string;
    activeQuestion: { id: string } | null;
  } | null;
  activePendingResolvedAnswers: Record<string, unknown> | null;
  activePendingIsResponding: boolean;
  activePendingDraftAnswers: Record<string, PendingUserInputDraftAnswer>;
  activePendingQuestionIndex: number;
  pendingUserInputError: string | null;
  respondingRequestIds: ApprovalRequestId[];

  readonly planProgress?: ComposerPlanProgress | null;
  showPlanFollowUpPrompt: boolean;
  activeProposedPlan: Thread["proposedPlans"][number] | null;
  activePlan: { turnId?: TurnId } | null;
  sidebarProposedPlan: { turnId?: TurnId } | null;
  planSidebarLabel: string;
  planSidebarOpen: boolean;

  // Mode
  runtimeMode: RuntimeMode;
  interactionMode: ProviderInteractionMode;

  // Provider / model
  resolvedProvider: ProviderKind;
  lockedProvider: ProviderKind | null;
  providerStatuses: ServerProvider[];
  /** Why the picker may withhold each provider it shows, per `ProviderModelPicker`. */
  providerAvailability: ReadonlyArray<ModelPickerProviderAvailability>;
  activeProjectDefaultModelSelection: ModelSelection | null | undefined;

  // Run context for the composer's bottom strip, on a thread as well as on the
  // landing. Env mode is the workspace asked for, which can precede its path.
  activeProjectTitle: string | null;
  activeThreadWorktreePath: string | null;
  activeThreadBranch: string | null;
  activeThreadEnvMode: DraftThreadEnvMode | null;
  /** The pull request the thread's branch is attached to, if any. */
  pullRequest: ChatComposerPrInput;
  activeThreadModelSelection: ModelSelection | null | undefined;
  composerDisabledReason?: string | false | null;
  /** Exact CMMD Admin cloud policy, or `null` for standalone/runtime inventory authority. */
  orgProviderPolicy: CmmdOrgProviderPolicy | null;

  // Context window
  activeThreadActivities: Thread["activities"] | undefined;

  // Kept so slash-command hints and shortcuts inside the composer stay accurate.
  // Play/Add and "Open in…" now live in the action strip `ChatView` renders.
  keybindings: ResolvedKeybindingsConfig;

  // Misc
  resolvedTheme: "light" | "dark";
  settings: UnifiedSettings;
  gitCwd: string | null;

  // Refs the parent needs kept in sync
  promptRef: React.MutableRefObject<string>;
  composerImagesRef: React.MutableRefObject<ComposerImageAttachment[]>;
  composerTerminalContextsRef: React.MutableRefObject<TerminalContextDraft[]>;

  // Scroll
  shouldAutoScrollRef: React.MutableRefObject<boolean>;
  scheduleStickToBottom: () => void;
  /** The timeline's scroll container, for the wheel gesture that rests the Composer. */
  getTimelineScrollNode: () => HTMLElement | null;
  /** Whether the timeline is showing its newest turn. */
  isTimelineAtEnd: () => boolean;

  // Callbacks
  onSend: (e?: { preventDefault: () => void }) => void;
  onOpenUsageLimits?: () => boolean;
  onInterrupt: () => void;
  onImplementPlanInNewThread: () => void;
  onRespondToApproval: (
    requestId: ApprovalRequestId,
    decision: ProviderApprovalDecision,
  ) => Promise<void>;
  onRetryGitHubActionRequired: (actionRequired: GitHubActionRequiredRequest) => Promise<void>;
  onSelectActivePendingUserInputOption: (questionId: string, optionLabel: string) => void;
  onAdvanceActivePendingUserInput: () => void;
  onQuestionFilesChange?:
    | ((questionId: string, files: ComposerFileAttachment[]) => void)
    | undefined;
  onPreviousActivePendingUserInputQuestion: () => void;
  onChangeActivePendingUserInputCustomAnswer: (
    questionId: string,
    value: string,
    nextCursor: number,
    expandedCursor: number,
    cursorAdjacentToMention: boolean,
  ) => void;

  onProviderModelSelect: (provider: ProviderKind, model: string) => void;
  toggleInteractionMode: () => void;
  handleRuntimeModeChange: (mode: RuntimeMode) => void;
  handleInteractionModeChange: (mode: ProviderInteractionMode) => void;
  togglePlanSidebar: () => void;

  focusComposer: () => void;
  scheduleComposerFocus: () => void;
  setThreadError: (threadId: ThreadId | null, error: string | null) => void;
  onExpandImage: (preview: ExpandedImagePreview) => void;
}

// --------------------------------------------------------------------------
// Component
// --------------------------------------------------------------------------

// fallow-ignore-next-line complexity -- port of upstream ChatComposer.tsx at pin e3e7cc3f; the component's decision surface is upstream's shape and refactoring it would break the provenance pin (ticket 3526).
export const ChatComposer = memo(
  forwardRef<ChatComposerHandle, ChatComposerProps>(function ChatComposer(props, ref) {
    const {
      composerDraftTarget,
      environmentId,
      routeKind,
      routeThreadRef,
      draftId,
      activeThreadId,
      activeThread,
      isServerThread,
      phase,
      isConnecting,
      isSendBusy,
      isPreparingWorktree,
      activePendingApproval,
      activeActionRequired,
      pendingApprovals,
      pendingUserInputs,
      activePendingProgress,
      activePendingResolvedAnswers,
      activePendingIsResponding,
      activePendingDraftAnswers,
      activePendingQuestionIndex,
      pendingUserInputError,
      respondingRequestIds,
      showPlanFollowUpPrompt,
      activeProposedPlan,
      activePlan,
      planProgress,
      sidebarProposedPlan,
      planSidebarLabel,
      planSidebarOpen,
      runtimeMode,
      interactionMode,
      resolvedProvider,
      lockedProvider,
      providerStatuses,
      providerAvailability,
      activeProjectDefaultModelSelection,
      activeProjectTitle,
      activeThreadWorktreePath,
      activeThreadBranch,
      activeThreadEnvMode,
      pullRequest,
      activeThreadModelSelection,
      composerDisabledReason = null,
      orgProviderPolicy,
      activeThreadActivities,
      keybindings: _keybindings,
      resolvedTheme,
      settings,
      gitCwd,
      promptRef,
      composerImagesRef,
      composerTerminalContextsRef,
      shouldAutoScrollRef,
      scheduleStickToBottom,
      getTimelineScrollNode,
      isTimelineAtEnd,
      onSend,
      onOpenUsageLimits = cannotOpenUsageLimits,
      onInterrupt,
      onImplementPlanInNewThread,
      onRespondToApproval,
      onRetryGitHubActionRequired,
      onSelectActivePendingUserInputOption,
      onAdvanceActivePendingUserInput,
      onQuestionFilesChange = () => {},
      onPreviousActivePendingUserInputQuestion,
      onChangeActivePendingUserInputCustomAnswer,
      onProviderModelSelect,
      toggleInteractionMode,
      handleRuntimeModeChange,
      handleInteractionModeChange,
      togglePlanSidebar,
      focusComposer,
      scheduleComposerFocus,
      setThreadError,
      onExpandImage,
    } = props;

    const availableProviderOptions = useMemo(
      () => getAvailableProviderOptions(orgProviderPolicy),
      [orgProviderPolicy],
    );

    const composerDraft = useComposerThreadDraft(composerDraftTarget);
    const prompt = composerDraft.prompt;
    const composerImages = composerDraft.images;
    const composerFiles = composerDraft.files ?? EMPTY_FILES;
    const composerTerminalContexts = composerDraft.terminalContexts;
    const nonPersistedComposerImageIds = composerDraft.nonPersistedImageIds;

    const setComposerDraftPrompt = useComposerDraftStore((store) => store.setPrompt);
    const addComposerDraftImages = useComposerDraftStore((store) => store.addImages);
    const addComposerDraftFiles = useComposerDraftStore((store) => store.addFiles);
    const setComposerDraftFiles = useComposerDraftStore((store) => store.setFiles);
    const removeComposerDraftImage = useComposerDraftStore((store) => store.removeImage);
    const insertComposerDraftTerminalContext = useComposerDraftStore(
      (store) => store.insertTerminalContext,
    );
    const removeComposerDraftTerminalContext = useComposerDraftStore(
      (store) => store.removeTerminalContext,
    );
    const setComposerDraftTerminalContexts = useComposerDraftStore(
      (store) => store.setTerminalContexts,
    );
    const clearComposerDraftPersistedAttachments = useComposerDraftStore(
      (store) => store.clearPersistedAttachments,
    );
    const syncComposerDraftPersistedAttachments = useComposerDraftStore(
      (store) => store.syncPersistedAttachments,
    );
    const getComposerDraft = useComposerDraftStore((store) => store.getComposerDraft);

    // ------------------------------------------------------------------
    // Model state
    // ------------------------------------------------------------------
    // The orchestrator resolves draft/thread preference through the exact access predicate.
    // Never re-resolve the stale draft preference from filtered inventory here.
    // ChatViewOrchestrator composes the same value from the same store slice and
    // hands it to submission, so the two cannot disagree within a commit.
    const composerModelSelection = useComposerModelSelection({
      threadRef: composerDraftTarget,
      providers: providerStatuses,
      resolvedProvider,
      lockedProvider,
      threadModelSelection: activeThreadModelSelection,
      projectModelSelection: activeProjectDefaultModelSelection,
      settings,
    });
    const {
      provider: selectedProvider,
      model: selectedModel,
      models: selectedProviderModels,
      modelOptions: composerModelOptions,
      providerAccountId: selectedProviderAccountId,
    } = composerModelSelection;

    const selectedProviderStatus = useMemo(
      () => providerStatuses.find((provider) => provider.provider === selectedProvider),
      [providerStatuses, selectedProvider],
    );

    // The ultrathink glow keys off the live Draft text, so it is recomputed here
    // rather than inside useComposerModelSelection: this component already
    // re-renders on every keystroke, and its callers must not have to.
    const composerProviderState = useMemo(
      () =>
        getComposerProviderState({
          provider: selectedProvider,
          model: selectedModel,
          models: selectedProviderModels,
          prompt,
          modelOptions: composerModelOptions,
        }),
      [composerModelOptions, prompt, selectedModel, selectedProvider, selectedProviderModels],
    );

    const selectedModelForPicker = selectedModel;
    const modelOptionsByProvider = useMemo<
      Record<ProviderKind, ReadonlyArray<ServerProvider["models"][number]>>
    >(
      () => buildComposerModelOptionsByProvider(providerStatuses, orgProviderPolicy),
      [orgProviderPolicy, providerStatuses],
    );
    // If the selected slug isn't in the current provider's option list,
    // normalize it so the picker trigger still renders a sensible label
    // (handles custom-model entries that haven't been added to provider state).
    const selectedModelForPickerWithCustomFallback = useMemo(() => {
      return resolveSelectedModelForPicker({
        modelOptionsByProvider,
        selectedProvider,
        selectedModel: selectedModelForPicker,
        normalizeModelSlug,
      });
    }, [modelOptionsByProvider, selectedModelForPicker, selectedProvider]);
    const searchableModelOptions = useMemo(
      () =>
        buildSearchableModelOptions(
          availableProviderOptions,
          lockedProvider,
          modelOptionsByProvider,
        ),
      [availableProviderOptions, lockedProvider, modelOptionsByProvider],
    );

    // ------------------------------------------------------------------
    // Context window
    // ------------------------------------------------------------------
    const activeContextWindow = useMemo(
      () => deriveLatestContextWindowSnapshot(activeThreadActivities ?? []),
      [activeThreadActivities],
    );

    // ------------------------------------------------------------------
    // Composer-local state
    // ------------------------------------------------------------------
    const [composerCursor, setComposerCursor] = useState(() =>
      collapseExpandedComposerCursor(prompt, prompt.length),
    );
    const [composerTrigger, setComposerTrigger] = useState<ComposerTrigger | null>(() =>
      detectComposerTrigger(prompt, prompt.length),
    );
    const composerCursorRef = useRef(composerCursor);
    const composerTriggerRef = useRef(composerTrigger);
    const [composerHighlightedItemId, setComposerHighlightedItemId] = useState<string | null>(null);
    // Shown only after a send is attempted. Warning while someone is still
    // typing a long prompt scolds them for work in progress; the ceiling only
    // matters at the moment they try to cross it.
    const [showPromptLengthError, setShowPromptLengthError] = useState(false);

    // The browser panel can arm an element target, but until now that was only
    // visible inside that panel — move to the composer and nothing said a
    // target was still attached. Read it here so the composer can state it and
    // let the user drop it without navigating back.
    const browserCommentTarget = useBrowserCommentDraftStore(
      (state) => selectBrowserCommentDraft(state.draftByThreadKey, routeThreadRef).target,
    );
    const clearBrowserCommentTarget = useBrowserCommentDraftStore((state) => state.setTarget);
    const [composerHighlightedSearchKey, setComposerHighlightedSearchKey] = useState<string | null>(
      null,
    );

    // ------------------------------------------------------------------
    // Refs
    // ------------------------------------------------------------------
    const composerEditorRef = useRef<ComposerPromptEditorHandle>(null);
    const composerFormRef = useRef<HTMLFormElement>(null);
    const composerSelectLockRef = useRef(false);
    const composerMenuOpenRef = useRef(false);
    const composerMenuItemsRef = useRef<ComposerCommandItem[]>([]);
    const activeComposerMenuItemRef = useRef<ComposerCommandItem | null>(null);

    // ------------------------------------------------------------------
    // Derived: composer send state
    // ------------------------------------------------------------------
    const composerSendState = useMemo(
      () =>
        deriveComposerSendState({
          prompt,
          imageCount: composerImages.length,
          fileCount: composerFiles.length,
          terminalContexts: composerTerminalContexts,
        }),
      [composerFiles.length, composerImages.length, composerTerminalContexts, prompt],
    );

    // ------------------------------------------------------------------
    // Derived: composer trigger / menu
    // ------------------------------------------------------------------
    const composerTriggerKind = composerTrigger?.kind ?? null;
    const pathTriggerQuery = composerTrigger?.kind === "path" ? composerTrigger.query : "";
    const isPathTrigger = composerTriggerKind === "path";
    const [debouncedPathQuery, composerPathQueryDebouncer] = useDebouncedValue(
      pathTriggerQuery,
      { wait: COMPOSER_PATH_QUERY_DEBOUNCE_MS },
      (debouncerState) => ({ isPending: debouncerState.isPending }),
    );
    const effectivePathQuery = pathTriggerQuery.length > 0 ? debouncedPathQuery : "";
    const workspaceEntriesQuery = useQuery(
      projectSearchEntriesQueryOptions({
        environmentId,
        cwd: gitCwd,
        query: effectivePathQuery,
        enabled: isPathTrigger,
        limit: 80,
      }),
    );
    const workspaceEntries = workspaceEntriesQuery.data?.entries ?? EMPTY_PROJECT_ENTRIES;

    const composerMenuItems = useMemo<ComposerCommandItem[]>(() => {
      return buildComposerMenuItems({
        trigger: composerTrigger,
        workspaceEntries,
        searchableModelOptions,
        selectedProvider,
        selectedProviderStatus,
      });
    }, [
      composerTrigger,
      searchableModelOptions,
      selectedProvider,
      selectedProviderStatus,
      workspaceEntries,
    ]);

    const composerMenuOpen = Boolean(composerTrigger);
    const composerMenuSearchKey = buildComposerMenuSearchKey(composerTrigger);
    const activeComposerMenuItem = useMemo(() => {
      const activeItemId = resolveComposerMenuActiveItemId({
        items: composerMenuItems,
        highlightedItemId: composerHighlightedItemId,
        currentSearchKey: composerMenuSearchKey,
        highlightedSearchKey: composerHighlightedSearchKey,
      });
      return composerMenuItems.find((item) => item.id === activeItemId) ?? null;
    }, [
      composerHighlightedItemId,
      composerHighlightedSearchKey,
      composerMenuItems,
      composerMenuSearchKey,
    ]);

    composerMenuOpenRef.current = composerMenuOpen;
    composerMenuItemsRef.current = composerMenuItems;
    activeComposerMenuItemRef.current = activeComposerMenuItem;

    const nonPersistedComposerImageIdSet = new Set(nonPersistedComposerImageIds);

    const isComposerApprovalState = activePendingApproval !== null;
    const isComposerDisabled = composerDisabledReason !== null;
    const stash = useComposerStashIntegration(
      composerDraftTarget,
      isComposerDisabled || isComposerApprovalState,
      (message, type) => toastManager.add({ title: message, type }),
    );
    const activePendingUserInput = pendingUserInputs[0] ?? null;
    const composerHasHeader = hasComposerHeader({
      isComposerDisabled,
      isComposerApprovalState,
      hasActiveActionRequired: activeActionRequired !== null,
      pendingUserInputCount: pendingUserInputs.length,
      showPlanFollowUpPrompt,
      hasActiveProposedPlan: activeProposedPlan !== null,
    });

    const composerFooterHasWideActions = showPlanFollowUpPrompt || activePendingProgress !== null;
    const showPlanSidebarToggle = Boolean(activePlan || sidebarProposedPlan || planSidebarOpen);
    const composerFooterActionLayoutKey = getComposerFooterActionLayoutKey({
      activePendingProgress,
      activePendingIsResponding,
      phase,
      showPlanFollowUpPrompt,
      prompt,
      hasSendableContent: composerSendState.hasSendableContent,
      isSendBusy,
      isConnecting,
      isPreparingWorktree,
    });

    const isComposerMenuLoading = isComposerPathMenuLoading({
      triggerKind: composerTriggerKind,
      pathTriggerQuery,
      isDebouncePending: composerPathQueryDebouncer.state.isPending,
      isWorkspaceQueryLoading: workspaceEntriesQuery.isLoading,
      isWorkspaceQueryFetching: workspaceEntriesQuery.isFetching,
    });
    const composerMenuEmptyState = getComposerMenuEmptyState(composerTriggerKind);
    const cmmdProjectId =
      isServerThread &&
      activeThread?.kind === "master" &&
      /^\d+$/.test(String(activeThread.projectId))
        ? String(activeThread.projectId)
        : null;
    const composerTaskAnchor = {
      cmmdProjectId,
      environmentId,
      activeThreadId,
      taskAnchorId: activeThread?.anchorKind === "task" ? activeThread.anchorCmmdId : null,
      taskAnchorDisabled: phase === "running" || isConnecting || isSendBusy,
    };

    // ------------------------------------------------------------------
    // Prompt helpers
    // ------------------------------------------------------------------
    const setComposerCursorIfChanged = useCallback((nextCursor: number) => {
      if (composerCursorRef.current === nextCursor) {
        return;
      }
      composerCursorRef.current = nextCursor;
      setComposerCursor(nextCursor);
    }, []);

    const setComposerTriggerIfChanged = useCallback((nextTrigger: ComposerTrigger | null) => {
      if (composerTriggersEqual(composerTriggerRef.current, nextTrigger)) {
        return;
      }
      composerTriggerRef.current = nextTrigger;
      setComposerTrigger(nextTrigger);
    }, []);

    const setPrompt = useMemo(
      () => synchronizeComposerPrompt(promptRef, setComposerDraftPrompt, composerDraftTarget),
      [composerDraftTarget, promptRef, setComposerDraftPrompt],
    );

    // ------------------------------------------------------------------
    // Provider traits UI
    // ------------------------------------------------------------------
    const { setPromptFromTraits, navigatePromptHistory } = useComposerPromptNavigation({
      environmentId,
      activeThreadId,
      promptRef,
      composerDraftTarget,
      setComposerDraftPrompt,
      setComposerCursorIfChanged,
      setComposerTriggerIfChanged,
      scheduleComposerFocus,
      composerDraft,
      historyDisabled: activePendingProgress != null,
    });

    const providerTraits = renderProviderTraits({
      provider: selectedProvider,
      ...(routeKind === "server" ? { threadRef: routeThreadRef } : {}),
      ...(routeKind === "draft" && draftId ? { draftId } : {}),
      model: selectedModel,
      models: selectedProviderModels,
      modelOptions: composerModelOptions?.[selectedProvider],
      prompt,
      onPromptChange: setPromptFromTraits,
    });
    const pendingPrimaryAction = derivePendingPrimaryAction({
      progress: activePendingProgress,
      isResponding: activePendingIsResponding,
      resolvedAnswers: activePendingResolvedAnswers,
    });

    const {
      imageAttachments,
      addComposerImageFiles,
      removeComposerImageFromDraft,
      removeComposerFileFromDraft,
    } = useChatComposerAttachments({
      composerDraftTarget,
      composerImages,
      composerFiles,
      routeKind,
      draftId,
      environmentId,
      activeThreadId,
      pendingUserInputCount: pendingUserInputs.length,
      addComposerDraftImages,
      addComposerDraftFiles,
      removeComposerDraftImage,
      setComposerDraftFiles,
      getComposerDraft,
      setThreadError,
      focusComposer,
    });

    useEffect(() => {
      void sweepUnreferencedComposerFiles();
    }, []);

    const removeComposerTerminalContextFromDraft = useRemoveComposerTerminalContext({
      composerDraftTarget,
      composerTerminalContexts,
      promptRef,
      removeComposerDraftTerminalContext,
      setPrompt,
      setComposerCursorIfChanged,
      setComposerTriggerIfChanged,
    });

    // ------------------------------------------------------------------
    // Sync refs back to parent
    // ------------------------------------------------------------------
    useEffect(() => {
      promptRef.current = prompt;
      const cursor = clampCollapsedComposerCursor(prompt, composerCursorRef.current);
      setComposerCursorIfChanged(cursor);
      setComposerTriggerIfChanged(
        detectComposerTrigger(prompt, expandCollapsedComposerCursor(prompt, cursor)),
      );
    }, [prompt, promptRef, setComposerCursorIfChanged, setComposerTriggerIfChanged]);

    useEffect(() => {
      composerImagesRef.current = composerImages;
    }, [composerImages, composerImagesRef]);

    useEffect(() => {
      composerTerminalContextsRef.current = composerTerminalContexts;
    }, [composerTerminalContexts, composerTerminalContextsRef]);

    // ------------------------------------------------------------------
    // Composer menu highlight sync
    // ------------------------------------------------------------------
    useEffect(() => {
      if (!composerMenuOpen) {
        setComposerHighlightedItemId(null);
        setComposerHighlightedSearchKey(null);
        return;
      }
      const nextActiveItemId = resolveComposerMenuActiveItemId({
        items: composerMenuItems,
        highlightedItemId: composerHighlightedItemId,
        currentSearchKey: composerMenuSearchKey,
        highlightedSearchKey: composerHighlightedSearchKey,
      });
      setComposerHighlightedItemId((existing) =>
        existing === nextActiveItemId ? existing : nextActiveItemId,
      );
      setComposerHighlightedSearchKey((existing) =>
        existing === composerMenuSearchKey ? existing : composerMenuSearchKey,
      );
    }, [
      composerHighlightedItemId,
      composerHighlightedSearchKey,
      composerMenuItems,
      composerMenuOpen,
      composerMenuSearchKey,
    ]);

    const lastSyncedPendingInputRef = useRef<{
      requestId: string | null;
      questionId: string | null;
    } | null>(null);

    useEffect(() => {
      const nextCustomAnswer = activePendingProgress?.customAnswer;
      if (typeof nextCustomAnswer !== "string") {
        lastSyncedPendingInputRef.current = null;
        return;
      }

      const nextRequestId = activePendingUserInput?.requestId ?? null;
      const nextQuestionId = activePendingProgress?.activeQuestion?.id ?? null;
      const questionChanged =
        lastSyncedPendingInputRef.current?.requestId !== nextRequestId ||
        lastSyncedPendingInputRef.current?.questionId !== nextQuestionId;
      const textChangedExternally = promptRef.current !== nextCustomAnswer;

      lastSyncedPendingInputRef.current = {
        requestId: nextRequestId,
        questionId: nextQuestionId,
      };

      if (!questionChanged && !textChangedExternally) {
        return;
      }

      promptRef.current = nextCustomAnswer;
      const nextCursor = collapseExpandedComposerCursor(nextCustomAnswer, nextCustomAnswer.length);
      setComposerCursorIfChanged(nextCursor);
      setComposerTriggerIfChanged(
        detectComposerTrigger(
          nextCustomAnswer,
          expandCollapsedComposerCursor(nextCustomAnswer, nextCursor),
        ),
      );
      setComposerHighlightedItemId(null);
    }, [
      activePendingProgress?.customAnswer,
      activePendingProgress?.activeQuestion?.id,
      activePendingUserInput?.requestId,
      promptRef,
      setComposerCursorIfChanged,
      setComposerTriggerIfChanged,
    ]);

    // ------------------------------------------------------------------
    // Reset compositor state on thread/draft change
    // ------------------------------------------------------------------
    useEffect(() => {
      setComposerHighlightedItemId(null);
      setComposerCursorIfChanged(
        collapseExpandedComposerCursor(promptRef.current, promptRef.current.length),
      );
      setComposerTriggerIfChanged(
        detectComposerTrigger(promptRef.current, promptRef.current.length),
      );
    }, [
      draftId,
      activeThreadId,
      promptRef,
      setComposerCursorIfChanged,
      setComposerTriggerIfChanged,
    ]);

    // ------------------------------------------------------------------
    // Footer compact layout observation
    // ------------------------------------------------------------------
    const {
      footerCompact: isComposerFooterCompact,
      primaryActionsCompact: isComposerPrimaryActionsCompact,
    } = useComposerFooterCompactness({
      composerFormRef,
      hasWideActions: composerFooterHasWideActions,
      actionLayoutKey: composerFooterActionLayoutKey,
      activeThreadId,
      shouldAutoScrollRef,
      scheduleStickToBottom,
    });

    // ------------------------------------------------------------------
    // Resting Composer
    // ------------------------------------------------------------------
    const resting = useRestingComposer({
      formRef: composerFormRef,
      // Every ChatComposer is a thread route, saved or local draft; the landing
      // is `NewThreadComposer`, which never calls this.
      isExistingThread: true,
      hasExpandedChrome:
        composerHasHeader ||
        composerMenuOpen ||
        imageAttachments.isDragOver ||
        showPromptLengthError,
      collapseOnBlur: settings.composerCollapseOnBlur,
      threadKey: String(activeThreadId ?? draftId ?? ""),
      getTimelineScrollNode,
      isTimelineAtEnd,
      focusEditor: scheduleComposerFocus,
    });

    useComposerImagePersistence({
      composerDraftTarget,
      composerImages,
      getComposerDraft,
      clearPersistedAttachments: clearComposerDraftPersistedAttachments,
      syncPersistedAttachments: syncComposerDraftPersistedAttachments,
    });

    // ------------------------------------------------------------------
    // Callbacks: prompt change
    // ------------------------------------------------------------------
    const onPromptChange = useCallback(
      (
        nextPrompt: string,
        nextCursor: number,
        expandedCursor: number,
        cursorAdjacentToMention: boolean,
        terminalContextIds: string[],
      ) => {
        if (activePendingProgress?.activeQuestion && pendingUserInputs.length > 0) {
          setComposerCursorIfChanged(nextCursor);
          setComposerTriggerIfChanged(
            cursorAdjacentToMention ? null : detectComposerTrigger(nextPrompt, expandedCursor),
          );
          onChangeActivePendingUserInputCustomAnswer(
            activePendingProgress.activeQuestion.id,
            nextPrompt,
            nextCursor,
            expandedCursor,
            cursorAdjacentToMention,
          );
          return;
        }
        setComposerCursorIfChanged(nextCursor);
        setComposerTriggerIfChanged(
          cursorAdjacentToMention ? null : detectComposerTrigger(nextPrompt, expandedCursor),
        );
        setPrompt(nextPrompt);
        if (!terminalContextIdListsEqual(composerTerminalContexts, terminalContextIds)) {
          setComposerDraftTerminalContexts(
            composerDraftTarget,
            syncTerminalContextsByIds(composerTerminalContexts, terminalContextIds),
          );
        }
      },
      [
        activePendingProgress?.activeQuestion,
        pendingUserInputs.length,
        onChangeActivePendingUserInputCustomAnswer,
        setComposerCursorIfChanged,
        setPrompt,
        setComposerTriggerIfChanged,
        composerDraftTarget,
        composerTerminalContexts,
        setComposerDraftTerminalContexts,
      ],
    );

    // ------------------------------------------------------------------
    // Callbacks: prompt replacement / menu
    // ------------------------------------------------------------------
    const applyPromptReplacement = useCallback(
      (
        rangeStart: number,
        rangeEnd: number,
        replacement: string,
        options?: { expectedText?: string },
      ): boolean => {
        const currentText = promptRef.current;
        const safeStart = Math.max(0, Math.min(currentText.length, rangeStart));
        const safeEnd = Math.max(safeStart, Math.min(currentText.length, rangeEnd));
        if (
          options?.expectedText !== undefined &&
          currentText.slice(safeStart, safeEnd) !== options.expectedText
        ) {
          return false;
        }
        const next = replaceTextRange(promptRef.current, rangeStart, rangeEnd, replacement);
        const nextCursor = collapseExpandedComposerCursor(next.text, next.cursor);
        const nextExpandedCursor = expandCollapsedComposerCursor(next.text, nextCursor);
        const activePendingQuestion = activePendingProgress?.activeQuestion;
        if (activePendingQuestion && activePendingUserInput) {
          promptRef.current = next.text;
          onChangeActivePendingUserInputCustomAnswer(
            activePendingQuestion.id,
            next.text,
            nextCursor,
            nextExpandedCursor,
            false,
          );
        } else {
          setPrompt(next.text);
        }
        setComposerCursorIfChanged(nextCursor);
        setComposerTriggerIfChanged(detectComposerTrigger(next.text, nextExpandedCursor));
        window.requestAnimationFrame(() => {
          composerEditorRef.current?.focusAt(nextCursor);
        });
        return true;
      },
      [
        activePendingProgress?.activeQuestion,
        activePendingUserInput,
        onChangeActivePendingUserInputCustomAnswer,
        promptRef,
        setComposerCursorIfChanged,
        setPrompt,
        setComposerTriggerIfChanged,
      ],
    );

    const readComposerSnapshot = useCallback((): {
      value: string;
      cursor: number;
      expandedCursor: number;
      terminalContextIds: string[];
    } => {
      const editorSnapshot = composerEditorRef.current?.readSnapshot();
      if (editorSnapshot) {
        return editorSnapshot;
      }
      return {
        value: promptRef.current,
        cursor: composerCursor,
        expandedCursor: expandCollapsedComposerCursor(promptRef.current, composerCursor),
        terminalContextIds: composerTerminalContexts.map((context) => context.id),
      };
    }, [composerCursor, composerTerminalContexts, promptRef]);

    const resolveActiveComposerTrigger = useCallback((): {
      snapshot: { value: string; cursor: number; expandedCursor: number };
      trigger: ComposerTrigger | null;
    } => {
      const snapshot = readComposerSnapshot();
      return {
        snapshot,
        trigger: detectComposerTrigger(snapshot.value, snapshot.expandedCursor),
      };
    }, [readComposerSnapshot]);

    const onSelectComposerItem = useComposerMenuItemSelection({
      selectLockRef: composerSelectLockRef,
      resolveActiveTrigger: resolveActiveComposerTrigger,
      applyPromptReplacement,
      setHighlightedItemId: setComposerHighlightedItemId,
      onOpenUsageLimits,
      handleInteractionModeChange,
      onProviderModelSelect,
    });

    const onComposerMenuItemHighlighted = useCallback(
      (itemId: string | null) => {
        setComposerHighlightedItemId(itemId);
        setComposerHighlightedSearchKey(composerMenuSearchKey);
      },
      [composerMenuSearchKey],
    );

    const nudgeComposerMenuHighlight = useCallback(
      (key: "ArrowDown" | "ArrowUp") => {
        setComposerHighlightedItemId(
          getNextComposerMenuHighlightId({
            items: composerMenuItems,
            highlightedItemId: composerHighlightedItemId,
            direction: key,
          }),
        );
      },
      [composerHighlightedItemId, composerMenuItems],
    );

    /** Routes command keys to the active menu, prompt history, mode toggle, or submission. */
    // Callback: command key
    // fallow-ignore-next-line complexity -- port of upstream ChatComposer.tsx at pin e3e7cc3f; the command-key dispatch is upstream's shape and extracting it would break the provenance pin (ticket 3526).
    const onComposerCommandKey = (
      key: "ArrowDown" | "ArrowUp" | "Enter" | "Tab",
      event: KeyboardEvent,
      editorValue: string,
    ) => {
      if (key === "Tab" && event.shiftKey) {
        toggleInteractionMode();
        return true;
      }
      const { trigger } = resolveActiveComposerTrigger();
      const menuIsActive = composerMenuOpenRef.current || trigger !== null;
      if (
        (key === "ArrowUp" || key === "ArrowDown") &&
        navigatePromptHistory(key, event, menuIsActive, composerEditorRef.current, editorValue)
      )
        return true;
      if (menuIsActive) {
        const currentItems = composerMenuItemsRef.current;
        const selectedItem = activeComposerMenuItemRef.current ?? currentItems[0];
        if (key === "ArrowDown" && currentItems.length > 0) {
          nudgeComposerMenuHighlight("ArrowDown");
          return true;
        }
        if (key === "ArrowUp" && currentItems.length > 0) {
          nudgeComposerMenuHighlight("ArrowUp");
          return true;
        }
        if ((key === "Enter" || key === "Tab") && selectedItem) {
          onSelectComposerItem(selectedItem);
          return true;
        }
      }
      if (key === "Enter" && !event.shiftKey) {
        if (!activePendingProgress?.activeQuestion) setPrompt(editorValue);
        submitWithLengthGuard();
        return true;
      }
      return false;
    };

    const handleInterruptPrimaryAction = useCallback(() => {
      void onInterrupt();
    }, [onInterrupt]);
    const handleImplementPlanInNewThreadPrimaryAction = useCallback(() => {
      void onImplementPlanInNewThread();
    }, [onImplementPlanInNewThread]);

    // ------------------------------------------------------------------
    // Imperative handle
    // ------------------------------------------------------------------
    useImperativeHandle(
      ref,
      () => ({
        addDroppedFiles: (files: File[]) => {
          addComposerImageFiles(files);
          focusComposer();
        },
        focusAtEnd: () => {
          composerEditorRef.current?.focusAtEnd();
        },
        restoreAfterTimelineReachedEnd: resting.restoreAfterTimelineReachedEnd,
        focusAt: (cursor: number) => {
          composerEditorRef.current?.focusAt(cursor);
        },
        readSnapshot: () => {
          return readComposerSnapshot();
        },
        resetCursorState: (options?: {
          cursor?: number;
          prompt?: string;
          detectTrigger?: boolean;
        }) => {
          const promptForState = options?.prompt ?? promptRef.current;
          const cursor = clampCollapsedComposerCursor(promptForState, options?.cursor ?? 0);
          setComposerHighlightedItemId(null);
          setComposerCursor(cursor);
          setComposerTrigger(
            options?.detectTrigger
              ? detectComposerTrigger(
                  promptForState,
                  expandCollapsedComposerCursor(promptForState, cursor),
                )
              : null,
          );
        },
        addTerminalContext: (selection: TerminalContextSelection) => {
          if (!activeThread) return;
          const snapshot = composerEditorRef.current?.readSnapshot() ?? {
            value: promptRef.current,
            cursor: composerCursor,
            expandedCursor: expandCollapsedComposerCursor(promptRef.current, composerCursor),
            terminalContextIds: composerTerminalContexts.map((context) => context.id),
          };
          const insertion = insertInlineTerminalContextPlaceholder(
            snapshot.value,
            snapshot.expandedCursor,
          );
          const nextCollapsedCursor = collapseExpandedComposerCursor(
            insertion.prompt,
            insertion.cursor,
          );
          const inserted = insertComposerDraftTerminalContext(
            composerDraftTarget,
            insertion.prompt,
            {
              id: randomUUID(),
              threadId: activeThread.id,
              createdAt: new Date().toISOString(),
              ...selection,
            },
            insertion.contextIndex,
          );
          if (!inserted) return;
          promptRef.current = insertion.prompt;
          setComposerCursor(nextCollapsedCursor);
          setComposerTrigger(detectComposerTrigger(insertion.prompt, insertion.cursor));
          window.requestAnimationFrame(() => {
            composerEditorRef.current?.focusAt(nextCollapsedCursor);
          });
        },
        getSendContext: () => ({
          prompt: promptRef.current,
          images: composerImagesRef.current,
          terminalContexts: composerTerminalContextsRef.current,
        }),
      }),
      [
        activeThread,
        addComposerImageFiles,
        composerDraftTarget,
        composerCursor,
        composerTerminalContexts,
        insertComposerDraftTerminalContext,
        promptRef,
        composerImagesRef,
        composerTerminalContextsRef,
        focusComposer,
        readComposerSnapshot,
        resting.restoreAfterTimelineReachedEnd,
      ],
    );

    const composerHeader = (
      <ChatComposerHeaderBanner
        disabledReason={props.contentPresentation === "submitted" ? null : composerDisabledReason}
        activePendingApproval={activePendingApproval}
        pendingApprovalCount={pendingApprovals.length}
        activeActionRequired={activeActionRequired}
        pendingUserInputs={pendingUserInputs}
        respondingRequestIds={respondingRequestIds}
        draftAnswers={activePendingDraftAnswers}
        questionIndex={activePendingQuestionIndex}
        error={pendingUserInputError}
        showPlanFollowUpPrompt={showPlanFollowUpPrompt}
        activeProposedPlan={activeProposedPlan}
        onRetryActionRequired={onRetryGitHubActionRequired}
        onToggleOption={onSelectActivePendingUserInputOption}
        onAdvance={onAdvanceActivePendingUserInput}
        onFilesChange={onQuestionFilesChange}
      />
    );

    const footerLeft = activePendingApproval ? null : (
      <ComposerFooter.Left
        accountSelection={{
          target: composerDraftTarget,
          modelSelection: composerModelSelection.modelSelection,
        }}
        footerCompact={isComposerFooterCompact && (!resting.isResting || resting.isMobileViewport)}
        isMobileViewport={resting.isMobileViewport}
        {...composerTaskAnchor}
        selectedProvider={selectedProvider}
        selectedModel={selectedModelForPickerWithCustomFallback}
        lockedProvider={lockedProvider}
        providerStatuses={providerStatuses}
        providerAvailability={providerAvailability}
        modelOptionsByProvider={modelOptionsByProvider}
        orgProviderPolicy={orgProviderPolicy}
        modelPickerIconClassName={composerProviderState.modelPickerIconClassName ?? null}
        interactionMode={interactionMode}
        runtimeMode={runtimeMode}
        showPlanSidebarToggle={showPlanSidebarToggle}
        planSidebarLabel={planSidebarLabel}
        planSidebarOpen={planSidebarOpen}
        providerTraitsPicker={providerTraits.picker}
        providerTraitsMenuContent={providerTraits.menuContent}
        onProviderModelChange={onProviderModelSelect}
        onToggleInteractionMode={toggleInteractionMode}
        onRuntimeModeChange={handleRuntimeModeChange}
        onTogglePlanSidebar={togglePlanSidebar}
        stash={stash.footer}
      />
    );

    const footerRight = (
      <ComposerFooter.Right
        primaryActionsCompact={isComposerPrimaryActionsCompact}
        pendingApprovalRequestId={activePendingApproval?.requestId ?? null}
        isRespondingToApproval={
          activePendingApproval !== null &&
          respondingRequestIds.includes(activePendingApproval.requestId)
        }
        onRespondToApproval={onRespondToApproval}
        hasPendingUserInput={pendingUserInputs.length > 0}
        activeContextWindow={activeContextWindow}
        activeThreadModelSelection={activeThreadModelSelection}
        activeThreadRateLimitStatus={activeThread?.rateLimitStatus}
        pendingAction={pendingPrimaryAction}
        isRunning={phase === "running"}
        showPlanFollowUpPrompt={shouldShowPlanFollowUpPrimaryAction({
          pendingUserInputCount: pendingUserInputs.length,
          showPlanFollowUpPrompt,
        })}
        promptHasText={prompt.trim().length > 0}
        isSendBusy={isSendBusy}
        isConnecting={isConnecting}
        isPreparingWorktree={isPreparingWorktree}
        hasSendableContent={composerSendState.hasSendableContent}
        isComposerDisabled={isComposerDisabled}
        onAttachImages={(files) => {
          addComposerImageFiles(files);
          focusComposer();
        }}
        onPreviousPendingQuestion={onPreviousActivePendingUserInputQuestion}
        onInterrupt={handleInterruptPrimaryAction}
        onImplementPlanInNewThread={handleImplementPlanInNewThreadPrimaryAction}
      />
    );

    // Guarded, not just displayed: the 120k ceiling is a schema constraint, so
    // without this the send round-trips and fails server-side with a generic
    // message that does not say how much to cut.
    const promptLengthError = getComposerPromptLengthValidationMessage(prompt);
    const { decision: creditGate, guardSubmit: submitWithLengthGuard } = useComposerSendGate({
      provider: selectedProvider,
      providerAccountId: selectedProviderAccountId,
      promptLengthError,
      onSend,
      onPromptLengthError: () => setShowPromptLengthError(true),
    });

    const contextStripFacts = buildChatComposerContextFacts({
      environmentId,
      projectTitle: activeProjectTitle,
      branch: activeThreadBranch,
      worktreePath: activeThreadWorktreePath,
      envMode: activeThreadEnvMode,
    });

    const showComposerAttachments = shouldShowComposerImages({
      isComposerApprovalState,
      pendingUserInputCount: pendingUserInputs.length,
    });

    return (
      <>
        {planProgress ? (
          <ComposerPlanProgressRow key={planProgress.identity} progress={planProgress} />
        ) : null}
        <ComposerSurface
          {...stash.surface}
          formRef={composerFormRef}
          onSubmit={submitWithLengthGuard}
          belowPrompt={
            <ChatComposerBelowPrompt
              browserCommentTarget={browserCommentTarget}
              promptLengthError={showPromptLengthError ? promptLengthError : null}
              creditGate={creditGate}
              onClearBrowserCommentTarget={() => clearBrowserCommentTarget(routeThreadRef, null)}
            />
          }
          contextStrip={buildChatComposerContextStrip({
            facts: contextStripFacts,
            pullRequest,
            controlsHostRef: resting.controlsHostRef,
            forceControlsHost: resting.isMobileViewport,
            ...composerTaskAnchor,
            goal: resolveComposerThreadGoal(props),
            showPlanSidebarToggle,
            planSidebarLabel,
            planSidebarOpen,
            onTogglePlanSidebar: togglePlanSidebar,
          })}
          frameClassName={composerProviderState.composerFrameClassName}
          surfaceClassName={composerProviderState.composerSurfaceClassName}
          isDragOver={imageAttachments.isDragOver}
          onDragEnter={imageAttachments.onDragEnter}
          onDragOver={imageAttachments.onDragOver}
          onDragLeave={imageAttachments.onDragLeave}
          onDrop={imageAttachments.onDrop}
          header={composerHeader}
          hasHeader={composerHasHeader}
          menu={{
            open: composerMenuOpen && !isComposerApprovalState,
            items: composerMenuItems,
            resolvedTheme,
            isLoading: isComposerMenuLoading,
            triggerKind: composerTriggerKind,
            groupSlashCommandSections:
              composerTrigger?.kind === "slash-command" &&
              composerTrigger.query.trim().length === 0,
            emptyStateText: composerMenuEmptyState,
            activeItemId: activeComposerMenuItem?.id ?? null,
            onHighlightedItemChange: onComposerMenuItemHighlighted,
            onSelect: onSelectComposerItem,
          }}
          images={
            showComposerAttachments
              ? props.contentPresentation === "submitted"
                ? []
                : composerImages
              : undefined
          }
          files={
            showComposerAttachments
              ? props.contentPresentation === "submitted"
                ? []
                : composerFiles
              : undefined
          }
          nonPersistedImageIds={nonPersistedComposerImageIdSet}
          onExpandImage={onExpandImage}
          onRemoveImage={removeComposerImageFromDraft}
          onRemoveFile={removeComposerFileFromDraft}
          promptEditor={{
            ref: composerEditorRef,
            value: getComposerPromptValue({
              isComposerApprovalState,
              activePendingCustomAnswer: activePendingProgress?.customAnswer ?? null,
              prompt: props.contentPresentation === "submitted" ? "" : prompt,
            }),
            cursor: composerCursor,
            terminalContexts: showComposerAttachments
              ? props.contentPresentation === "submitted"
                ? []
                : composerTerminalContexts
              : [],
            skills: selectedProviderStatus?.skills ?? [],
            onRemoveTerminalContext: removeComposerTerminalContextFromDraft,
            onChange: onPromptChange,
            onCommandKeyDown: onComposerCommandKey,
            onPaste: imageAttachments.onPaste,
            placeholder: getComposerPromptPlaceholder({
              isComposerApprovalState,
              activeApprovalDetail: activePendingApproval?.detail ?? null,
              hasActivePendingProgress: activePendingProgress !== null,
              showPlanFollowUpPrompt,
              hasActiveProposedPlan: activeProposedPlan !== null,
              phase,
            }),
            disabled: isComposerDisabled || isConnecting || isComposerApprovalState,
          }}
          footerLeft={resting.isResting ? null : footerLeft}
          footerRight={footerRight}
          footerCompact={isComposerFooterCompact}
          primaryActionsCompact={isComposerPrimaryActionsCompact}
          resting={resting.isResting}
        />
        {resting.isResting ? (
          <RestingComposerControlsPortal {...resting}>{footerLeft}</RestingComposerControlsPortal>
        ) : null}
      </>
    );
  }),
);
